import { useImperativeHandle, type Ref, type RefObject } from "react";

export interface TextareaInsertHandle {
  insertAtCursor: (text: string) => void;
  focus: () => void;
}

interface Args {
  textareaRef: RefObject<HTMLTextAreaElement | null>;
  value: string;
  onChange: (next: string) => void;
}

// Прокидывает наружу два imperative-метода: вставка в позицию каретки и фокус.
// Используется родителем (TemplateEditor + общий сайдбар), чтобы клик по чипу
// переменной вставлял плейсхолдер ровно туда, где стоит курсор пользователя.
export const useTextareaInsertHandle = (
  ref: Ref<TextareaInsertHandle>,
  { textareaRef, value, onChange }: Args,
) => {
  useImperativeHandle(
    ref,
    () => ({
      insertAtCursor: (text: string) => {
        const el = textareaRef.current;
        if (!el) {
          onChange(value + text);
          return;
        }
        const start = el.selectionStart ?? value.length;
        const end = el.selectionEnd ?? value.length;
        onChange(value.slice(0, start) + text + value.slice(end));
        // Курсор ставим сразу после вставленного блока, чтобы юзер мог
        // продолжать печатать без лишних кликов. requestAnimationFrame даёт
        // React закоммитить новое value, прежде чем мы трогаем selection.
        requestAnimationFrame(() => {
          el.focus();
          const pos = start + text.length;
          el.setSelectionRange(pos, pos);
        });
      },
      focus: () => textareaRef.current?.focus(),
    }),
    [textareaRef, value, onChange],
  );
};
